import { isString } from '@art-suite/art-core-ts';
import { getUniqueId } from '../commonLib';
import { ScanTemplateValue, ScanTemplateValues } from '../ScanTemplateRoot';
import { STANDARD_LINE_ITEM_VALUE_NAMES, StandardScanTemplateLineItem } from './StandardScanTemplateSchemas';

export type StandardLineItemCostType = 'material' | 'labor' | 'materialAndLabor';

export type StandardLineItemSkuMode =
  | 'none' // no SKU; the author sets the price directly
  | 'sku' // SKU(s) chosen at template time (skuIdOptions); the price comes from the selected SKU
  | 'scopeTimeSku'; // the field user picks any catalog SKU at the scope stage (useScopeStageSkus)

export type StandardLineItemGeneratorOptions = {
  costType: StandardLineItemCostType;
  skuMode: StandardLineItemSkuMode;
  /** string: the measurement's equation; true: measurement with default value 0 */
  measurement?: string | true;
  /** adds an overage value: a fraction always added on top of the measurement (0.2 = +20%) */
  overage?: boolean;
  /** adds a coverage value: how many measurement units one purchased unit covers (e.g. 200 sqft per bucket) */
  coverage?: boolean;
};

/** The standard totalCost value for a line item with the given cost values. */
export const getStandardTotalCostValue = (hasMaterial: boolean, hasLabor: boolean): ScanTemplateValue => ({
  type: 'usd',
  value: hasMaterial && hasLabor ? 'materialCost + laborCost' : hasMaterial ? 'materialCost' : 'laborCost',
});

const validateStandardLineItemGeneratorOptions = (
  { costType, skuMode, measurement, overage, coverage }: StandardLineItemGeneratorOptions,
): void => {
  if ((overage || coverage) && !measurement) {
    throw new Error('overage and coverage require measurement');
  }
  if (costType === 'labor' && skuMode !== 'none') {
    throw new Error('labor-only line items cannot have a SKU (a SKU implies a materialCost)');
  }
};

/**
 * Generates a complete, valid standard line item for the requested shape.
 *
 * Values come in two kinds:
 * - user-owned inputs (laborPrice, measurement, overage, coverage,
 *   quantity when not measurement-driven, and price when there is no product):
 *   carried over from basedOn when present, otherwise given a default
 * - derived values (materialCost, laborCost, totalCost, and quantity when
 *   measurement-driven): always regenerated with the standard equations
 *
 * Values on basedOn with names this generator doesn't manage pass through
 * untouched, as do all non-value properties (id, name, description, ...).
 * This makes it safe to regenerate an existing line item after the user
 * changes one of the options: getStandardLineItem(newOptions, currentLineItem).
 */
export const getStandardLineItem = (
  options: StandardLineItemGeneratorOptions,
  basedOn?: Partial<StandardScanTemplateLineItem>,
): StandardScanTemplateLineItem => {
  validateStandardLineItemGeneratorOptions(options);
  const { costType, skuMode, measurement, overage, coverage } = options;
  const hasMaterial = costType !== 'labor';
  const hasLabor = costType !== 'material';
  const basedOnValues: ScanTemplateValues = basedOn?.values ?? {};

  const carry = (name: string, defaultValue: ScanTemplateValue): ScanTemplateValue => basedOnValues[name] ?? defaultValue;

  const values: ScanTemplateValues = {};
  for (const [name, value] of Object.entries(basedOnValues)) {
    if (!STANDARD_LINE_ITEM_VALUE_NAMES.includes(name)) values[name] = value;
  }

  if (measurement) {
    const carriedMeasurement = carry('measurement', { type: 'sqft', value: 0 });
    values.measurement = isString(measurement) ? { ...carriedMeasurement, value: measurement } : carriedMeasurement;
    if (overage) values.overage = carry('overage', { type: 'count', value: 0 });
    if (coverage) values.coverage = carry('coverage', { type: 'count', value: 1 });

    // quantity is the derived purchase quantity, rounded up to whole purchase units when coverage
    // is configured. It is always a count: the measurement carries the unit.
    const measuredAmount = overage ? 'measurement * (1 + overage)' : 'measurement';
    values.quantity = { type: 'count', value: coverage ? `ceil((${measuredAmount}) / coverage)` : measuredAmount };
  } else {
    // if basedOn was measurement-driven its quantity was derived, so reset it rather than carry a dangling equation
    values.quantity = basedOnValues.measurement ? { type: 'count', value: 0 } : carry('quantity', { type: 'count', value: 0 });
  }

  if (hasMaterial) {
    // with a product (a SKU, or one picked at scope time) the price always comes from the product;
    // without one it is the author's, carried only when basedOn had no product either
    const noProductPrice: ScanTemplateValue = { type: 'usd', value: 0 };
    values.price = skuMode !== 'none'
      ? { type: 'usd' }
      : basedOn && getStandardLineItemGeneratorOptions(basedOn).skuMode !== 'none'
      ? noProductPrice
      : carry('price', noProductPrice);
    values.materialCost = { type: 'usd', value: 'price * quantity' };
  }
  if (hasLabor) {
    values.laborPrice = carry('laborPrice', { type: 'usd', value: 0 });
    // labor is priced against the measured amount (a painter is paid per sqft, not per bucket)
    values.laborCost = { type: 'usd', value: measurement ? 'laborPrice * measurement' : 'laborPrice * quantity' };
  }
  values.totalCost = getStandardTotalCostValue(hasMaterial, hasLabor);

  const { values: _values, skuIdOptions, useScopeStageSkus: _useScopeStageSkus, id, name, ...otherProps } = basedOn ?? {};
  return {
    ...otherProps,
    id: id ?? getUniqueId(),
    name: name ?? 'New Line Item',
    ...(skuMode === 'sku' ? { skuIdOptions: skuIdOptions ?? [] } : {}),
    ...(skuMode === 'scopeTimeSku' ? { useScopeStageSkus: true } : {}),
    values,
  };
};

/**
 * The inverse of getStandardLineItem: reads the generator options off an
 * existing line item so an editor can render its option controls and
 * round-trip through getStandardLineItem when the user changes one.
 */
export const getStandardLineItemGeneratorOptions = (
  lineItem: Pick<StandardScanTemplateLineItem, 'values' | 'skuIdOptions' | 'useScopeStageSkus'>,
): StandardLineItemGeneratorOptions => {
  const values = lineItem.values ?? {};
  const measurementValue = values.measurement?.value;
  return {
    costType: values.materialCost && values.laborCost ? 'materialAndLabor' : values.laborCost ? 'labor' : 'material',
    skuMode: lineItem.useScopeStageSkus ? 'scopeTimeSku' : lineItem.skuIdOptions?.length ? 'sku' : 'none', // an empty skuIdOptions array means no product (the editor's remove-product convention)
    ...(values.measurement ? { measurement: isString(measurementValue) ? measurementValue : true as const } : {}),
    ...(values.overage ? { overage: true } : {}),
    ...(values.coverage ? { coverage: true } : {}),
  };
};
